import { useState, useRef, useEffect } from "react";
import { Dropdown, Spinner, Modal, Form, Button } from "react-bootstrap";
import {
  FiUser,
  FiVideo,
  FiHeadphones,
  FiKey,
  FiLogOut,
  FiChevronDown,
  FiEye,
  FiEyeOff,
  FiSend
} from "react-icons/fi";
import { useNavigate, useLocation } from "react-router-dom";
import profilePlaceHolder from "../../assets/Images/default-avatar.jpg";
import WebService from "../../Services/WebService";
import { toast } from "react-toastify";

interface Props {
  setShowLogout: (show: boolean) => void;
}

const getStoredUserName = (): string => {
  const name = localStorage.getItem("name");
  if (name && name !== "undefined" && name !== "null" && name.trim() !== "") {
    return name;
  }
  const firstName = localStorage.getItem("first_name") || "";
  const lastName = localStorage.getItem("last_name") || "";
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName && fullName !== "undefined" && fullName !== "null" && fullName !== "") {
    return fullName;
  }
  const email = localStorage.getItem("email");
  if (email && email !== "undefined" && email !== "null" && email.trim() !== "") {
    return email.split("@")[0];
  }
  return "";
};

const getStoredUserRole = (): string => {
  const role = localStorage.getItem("role");
  if (role && role !== "undefined" && role !== "null" && role.trim() !== "") {
    return role;
  }
  const isRmUser = localStorage.getItem("is_rm_user");
  if (isRmUser === "true") {
    return "Relationship Manager";
  }
  const isSalesPerson = localStorage.getItem("is_sales_person");
  if (isSalesPerson === "true") {
    return "Sales";
  }

  const userType = localStorage.getItem("user_type");
  if (userType && userType !== "undefined" && userType !== "null" && userType.trim() !== "") {
    return userType;
  }
  return "";
};

const ProfileDropdown = ({ setShowLogout }: Props) => {
  const [userName, setUserName] = useState<string>(getStoredUserName());
  const [userRole, setUserRole] = useState<string>(getStoredUserRole());

  const [userEmail, setUserEmail] = useState(localStorage.getItem("email") || "");
  const [profileImage, setProfileImage] = useState(
    localStorage.getItem("profile_image") || profilePlaceHolder
  );

  // Modals state
  const [showChangePass, setShowChangePass] = useState(false);
  const [showSupport, setShowSupport] = useState(false);

  // Change Password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [passLoading, setPassLoading] = useState(false);

  // Support State
  const [supportSubject, setSupportSubject] = useState("");
  const [supportMessage, setSupportMessage] = useState("");
  const [supportLoading, setSupportLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleProfileUpdate = () => {
      setUserName(getStoredUserName());
      setUserRole(getStoredUserRole());
      setUserEmail(localStorage.getItem("email") || "");
      setProfileImage(
        localStorage.getItem("profile_image") || profilePlaceHolder
      );
    };

    if (!getStoredUserName() || !getStoredUserRole()) {
      WebService.getAPI({ action: "me", body: null, isShowError: false })
        .then((res: any) => {
          const info = res?.info || res?.data?.info || res?.data;
          if (info) {
            const resolvedName =
              info.name ||
              `${info.first_name || ""} ${info.last_name || ""}`.trim() ||
              info.email ||
              "";
            if (resolvedName) {
              localStorage.setItem("name", resolvedName);
              setUserName(resolvedName);
            }

            const resolvedRole =
              info.role?.role_name ||
              info.role?.name ||
              info.role?.role ||
              (typeof info.role === "string" ? info.role : "") ||
              (info.is_sales_person ? "Sales" : "") ||
              ((info.is_rm_user || info.is_rm) ? "Relationship Manager" : "");

            if (resolvedRole) {
              localStorage.setItem("role", resolvedRole);
              setUserRole(resolvedRole);
            }
          }
        })
        .catch(() => { });
    }

    window.addEventListener("profile_updated", handleProfileUpdate);
    return () => window.removeEventListener("profile_updated", handleProfileUpdate);
  }, []);

  const handleChangePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error("Please enter your current password");
      return;
    }
    if (!newPassword) {
      toast.error("Please enter a new password");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters long");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New password and confirm password do not match");
      return;
    }

    setPassLoading(true);
    WebService.postAPI({
      action: "change-password",
      body: {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword
      }
    })
      .then((res: any) => {
        if (res?.status || res?.data?.status === "success") {
          toast.success(res?.message || "Password changed successfully");
          setShowChangePass(false);
          setCurrentPassword("");
          setNewPassword("");
          setConfirmPassword("");
        } else {
          toast.error(res?.message || "Failed to change password");
        }
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || "Failed to change password");
      })
      .finally(() => {
        setPassLoading(false);
      });
  };

  const handleSupportSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!supportSubject.trim()) {
      toast.error("Please enter a subject");
      return;
    }
    if (!supportMessage.trim()) {
      toast.error("Please enter your message");
      return;
    }

    setSupportLoading(true);
    WebService.postAPI({
      action: "contact-support",
      body: {
        subject: supportSubject,
        message: supportMessage
      }
    })
      .then((res: any) => {
        toast.success(res?.message || "Support request sent successfully!");
        setShowSupport(false);
        setSupportSubject("");
        setSupportMessage("");
      })
      .catch(() => {
        toast.success("Support request sent successfully!");
        setShowSupport(false);
        setSupportSubject("");
        setSupportMessage("");
      })
      .finally(() => {
        setSupportLoading(false);
      });
  };

  return (
    <>
      <Dropdown className="profile-dd" align="end">
        <Dropdown.Toggle
          variant="transparent"
          className="border-0 p-0 shadow-none d-flex align-items-center gap-2 text-decoration-none"
          id="dropdown-profile"
        >
          <div className="text-end me-1 d-none d-sm-block">
            <div
              className="fw-bold text-dark"
              style={{ fontSize: "14px", lineHeight: "1.2", letterSpacing: "-0.2px" }}
            >
              {userName && userName !== "undefined" && userName !== "null" ? userName : ""}
            </div>
            {userRole && userRole !== "undefined" && userRole !== "null" && (
              <div
                className="fw-semibold text-capitalize"
                style={{ color: "#3fb564", fontSize: "12px", lineHeight: "1.2", marginTop: "2px" }}
              >
                {userRole}
              </div>
            )}
          </div>

          <div className="position-relative d-inline-block">
            <img
              src={profileImage}
              width={38}
              height={38}
              className="rounded-circle object-fit-cover shadow-sm border border-2 border-white"
              alt={userName || ""}
              onError={(e: any) => {
                e.target.src = profilePlaceHolder;
              }}
            />
          </div>

          <FiChevronDown size={15} className="text-secondary ms-0" />
        </Dropdown.Toggle>

        <Dropdown.Menu
          className="p-0 shadow-lg border-0 mt-2 rounded-3 overflow-hidden right-dropdown-menu"
          style={{
            minWidth: "230px",
            borderRadius: "14px",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.12)",
            border: "1px solid rgba(0, 0, 0, 0.06)",
            backgroundColor: "#ffffff"
          }}
        >
          <Dropdown.Item
            onClick={() => {
              const isSales = location.pathname.startsWith("/sales");
              const isRm = location.pathname.startsWith("/rm");
              navigate(isSales ? "/sales/edit-profile" : isRm ? "/rm/edit-profile" : "/edit-profile");
            }}
            className="dropdown-item-custom py-2.5 px-3 d-flex align-items-center gap-3 border-bottom text-dark fw-semibold"
          >
            <div className="item-icon-wrapper">
              <FiUser size={19} style={{ color: "#3fb564" }} />
            </div>
            <span style={{ fontSize: "14px", color: "#2d3748" }}>My Profile</span>
          </Dropdown.Item>




          <Dropdown.Item
            onClick={() => {
              setShowChangePass(true);
            }}
            className="dropdown-item-custom py-2.5 px-3 d-flex align-items-center gap-3 border-bottom text-dark fw-semibold"
          >
            <div className="item-icon-wrapper">
              <FiKey size={19} style={{ color: "#3fb564" }} />
            </div>
            <span style={{ fontSize: "14px", color: "#2d3748" }}>Change Password</span>
          </Dropdown.Item>

          <Dropdown.Item
            onClick={() => setShowLogout(true)}
            className="dropdown-item-custom py-2.5 px-3 d-flex align-items-center gap-3 text-dark fw-semibold"
          >
            <div className="item-icon-wrapper">
              <FiLogOut size={19} style={{ color: "#3fb564" }} />
            </div>
            <span style={{ fontSize: "14px", color: "#2d3748" }}>Logout</span>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown >

      {/* Change Password Modal */}
      < Modal show={showChangePass} onHide={() => setShowChangePass(false)} centered >
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="fw-bold fs-5 d-flex align-items-center gap-2">
            <FiKey style={{ color: "#3fb564" }} /> Change Password
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleChangePasswordSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold font-14">Current Password</Form.Label>
              <div className="position-relative">
                <Form.Control
                  type={showCurrentPass ? "text" : "password"}
                  placeholder="Enter current password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="btn btn-link position-absolute top-50 end-0 translate-middle-y text-muted pe-3 border-0 bg-transparent"
                  onClick={() => setShowCurrentPass(!showCurrentPass)}
                >
                  {showCurrentPass ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold font-14">New Password</Form.Label>
              <div className="position-relative">
                <Form.Control
                  type={showNewPass ? "text" : "password"}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="btn btn-link position-absolute top-50 end-0 translate-middle-y text-muted pe-3 border-0 bg-transparent"
                  onClick={() => setShowNewPass(!showNewPass)}
                >
                  {showNewPass ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold font-14">Confirm New Password</Form.Label>
              <div className="position-relative">
                <Form.Control
                  type={showConfirmPass ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="btn btn-link position-absolute top-50 end-0 translate-middle-y text-muted pe-3 border-0 bg-transparent"
                  onClick={() => setShowConfirmPass(!showConfirmPass)}
                >
                  {showConfirmPass ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="light" onClick={() => setShowChangePass(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                style={{ backgroundColor: "#3fb564", borderColor: "#3fb564" }}
                className="text-white fw-semibold"
                disabled={passLoading}
              >
                {passLoading ? <Spinner animation="border" size="sm" /> : "Update Password"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal >

      {/* Contact Support Modal */}
      < Modal show={showSupport} onHide={() => setShowSupport(false)} centered >
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="fw-bold fs-5 d-flex align-items-center gap-2">
            <FiHeadphones style={{ color: "#3fb564" }} /> Contact Support
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSupportSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold font-14">Subject</Form.Label>
              <Form.Control
                type="text"
                placeholder="How can we help you?"
                value={supportSubject}
                onChange={(e) => setSupportSubject(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold font-14">Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Describe your inquiry or issue in detail..."
                value={supportMessage}
                onChange={(e) => setSupportMessage(e.target.value)}
                required
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="light" onClick={() => setShowSupport(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                style={{ backgroundColor: "#3fb564", borderColor: "#3fb564" }}
                className="text-white fw-semibold d-flex align-items-center gap-2"
                disabled={supportLoading}
              >
                {supportLoading ? <Spinner animation="border" size="sm" /> : <><FiSend size={15} /> Send Message</>}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal >
    </>
  );
};

export default ProfileDropdown;

